import React, { useEffect, useMemo, useState } from "react";
import * as Type from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import * as Button from "../../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import Preloader, * as PreloaderStyles from "../../../../../../../common-deprecated/styles/v2/toyota/custom/Preloader";
import * as PacksView from "./styles/Packs";
import {
    IconWrapper,
    ToggleIconWrapper,
} from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import Icon from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import IconCard from "../../../../../../../common-deprecated/svg/toyota/aem/card.svg";
import { BnbLabel, useBnbLabel, useBnbOptionalLabel } from "../../../../utils/labels";
import Collapsable from "../../common/collapsable/Collapsable";
import * as FinanceStyles from "./styles/Finance";
import type { FinanceInsuranceCalculatorType } from "../../../../utils/FinanceInsuranceCalculatorLink";
import { FINANCE_CALCULATOR } from "../../../../../../../common-deprecated/constants";
import FinanceInsuranceCalculator from "../../../common/FinanceInsuranceCalculator";
import { useBnbSelector } from "../../../../redux/store";
import useFinanceToggle from "../../../../hooks/shared/actions/useFinanceToggle";
import { setFinanceCollapsable } from "../../../../redux/actions/FinanceInsuranceActions";
import { MODAL_FINANCE_DISCLAIMER } from "../../../../../../../common-deprecated/utils/modalConstants";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import { getBnbMonthlyRateFormat } from "../../../../utils/settings/bnbFinanceSettingUtils";
import MonthlyPrice from "../../common/price/MonthlyPrice";
import { showFinanceInsurance } from "../../../../utils/financeInsuranceUtils";
import SrOnly from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { trackClickedLink, trackInPageCalculator } from "../../../../utils/tracking";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

type PropsType = {
    calculatorType?: FinanceInsuranceCalculatorType;
    expandedByDefault?: boolean; // When provided the finance section is expanded by default
    showIntroduction?: boolean;
};
const Finance = ({
    calculatorType = FINANCE_CALCULATOR,
    expandedByDefault = false,
    showIntroduction = true,
}: PropsType): JSX.Element | null => {
    const finance = useBnbSelector((state) => state.finance);
    const { total, totalWithoutPromotions, collapsableActive, info } = finance;
    const { bnbSettings, car, commonSettings } = useBnbSelector((state) => state);
    const step = useBnbSelector((state) => state.navigation.step);
    const basketTotal = useBnbSelector((state) => state.basket.total);
    const modelCode = useBnbSelector((state) => state.car.model.internalCode || "");
    const [financeDetailsExpanded, setFinanceDetailsExpanded] = useState<boolean>(false);
    const { showMonthlyLoader, financeOption } = useFinanceToggle();
    const financeButtonEditLabel = useBnbOptionalLabel("financeButtonEdit");
    const financeCollapsableCtaLabel = useBnbLabel("financeCollapsableCta");

    const monthlyFormat = getBnbMonthlyRateFormat(commonSettings, modelCode);
    const dispatch = useDispatch();

    // Financial Information
    const totalPrice = useMemo(
        () => ({
            ...basketTotal,
            monthly: Number(total),
            monthlyWithoutPromotion: Number(totalWithoutPromotions),
        }),
        [basketTotal, total, totalWithoutPromotions],
    );

    const showTotalPrice =
        totalPrice &&
        totalPrice.monthly !== 0 &&
        (!bnbSettings.enableFinanceCompare || (bnbSettings.enableFinanceCompare && finance.quotes.finance));

    useEffect(() => {
        if (expandedByDefault && !collapsableActive) {
            dispatch(setFinanceCollapsable(true));
        }
    }, []);

    if (
        !showFinanceInsurance({
            type: "finance",
            bnbSettings,
            car,
            commonSettings,
            finance,
            step,
        })
    ) {
        return null;
    }

    // TRIGGERS AFTER THE COLLAPSABLE HAS EXPANDED/CLOSED
    const onChange = (expanded: boolean): void => {
        dispatch(setFinanceCollapsable(expanded));
    };

    // Only Triggers after the collapsable button is clicked to open/close the collapsable
    const onTrack = (): void => {
        const trackingLabel = !collapsableActive ? "open-inpage-finance-calculator" : "close-inpage-finance-calculator";
        trackInPageCalculator(!collapsableActive ? "cc-cta" : "cc-action", trackingLabel, step);
    };

    return (
        <FinanceStyles.Finance id="finance-injection-container">
            {showIntroduction && (
                <PacksView.Header>
                    <Flex.Col width={{ sm: 7 / 12, md: 1 }}>
                        <Type.H2 headingType="gamma">
                            <BnbLabel label="financeInPageTitle" />
                        </Type.H2>
                        <Type.Text my={3}>
                            <BnbLabel label="financeInPageText" useHTML />
                        </Type.Text>
                        <details
                            onToggle={() => {
                                setFinanceDetailsExpanded(!financeDetailsExpanded);
                                if (!financeDetailsExpanded) {
                                    trackClickedLink("finance-benefits");
                                }
                            }}
                        >
                            <FinanceStyles.Summary>
                                <Button.Link my={3} hasIconRight as="a">
                                    <BnbLabel label="financeInPageDetailsLabel" />
                                    <ToggleIconWrapper isDown={financeDetailsExpanded}>
                                        <Icon variant="chevronDown" />
                                    </ToggleIconWrapper>
                                </Button.Link>
                            </FinanceStyles.Summary>
                            <Type.SmallText my={3}>
                                <BnbLabel label="financeInPageDetails" useHTML />
                            </Type.SmallText>
                        </details>
                    </Flex.Col>
                </PacksView.Header>
            )}
            <Flex.Row my={4}>
                <Flex.Col width={1}>
                    <Collapsable
                        multiRowHeader
                        title={
                            <Type.H4 mb={0}>
                                <BnbLabel
                                    label={financeOption === "monthly" ? "financeCollapsableTitle" : "financeButton"}
                                />
                            </Type.H4>
                        }
                        subtitle={
                            showTotalPrice && (
                                <Flex.Row>
                                    <Flex.Col pr={0}>
                                        <MonthlyPrice
                                            price={totalPrice}
                                            priceFormat={monthlyFormat}
                                            showLoader={showMonthlyLoader}
                                            forceHideDisclaimer
                                            financeInfo={info}
                                            size="small"
                                        />
                                    </Flex.Col>
                                    {info && info.disclaimer && (
                                        <Flex.Col p={0}>
                                            <Button.Link
                                                type="button"
                                                hasIconRight
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    if (info?.disclaimer) {
                                                        dispatch(
                                                            showModal({
                                                                type: MODAL_FINANCE_DISCLAIMER,
                                                                disclaimer: info.disclaimer,
                                                                productName: "",
                                                            }),
                                                        );
                                                    }
                                                }}
                                            >
                                                <IconWrapper>
                                                    <Icon variant="info" />
                                                </IconWrapper>
                                                <SrOnly>
                                                    <BnbLabel label="bnbA11yButtonMoreInfo" />
                                                </SrOnly>
                                            </Button.Link>
                                        </Flex.Col>
                                    )}
                                </Flex.Row>
                            )
                        }
                        ctaLabel={showTotalPrice ? financeButtonEditLabel : financeCollapsableCtaLabel}
                        icon={
                            <IconWrapper>
                                <IconCard />
                            </IconWrapper>
                        }
                        content={
                            <FinanceInsuranceCalculator
                                type={calculatorType}
                                visible={collapsableActive}
                                showLoader={showMonthlyLoader}
                                loader={
                                    <PreloaderStyles.PreLoaderWrapper hasBorder hasButtonHeight>
                                        <Preloader size={30} />
                                    </PreloaderStyles.PreLoaderWrapper>
                                }
                            />
                        }
                        active={collapsableActive}
                        onChange={onChange}
                        onTrack={onTrack}
                    />
                </Flex.Col>
            </Flex.Row>
        </FinanceStyles.Finance>
    );
};

export default Finance;
